import { useEffect, useState } from "react";

interface RabbitProgressProps {
  loading: boolean;
  estimatedSeconds?: number;
}

export default function RabbitProgress({ loading, estimatedSeconds = 60 }: RabbitProgressProps) {
  const [progress, setProgress] = useState(0);
  const [eating, setEating] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!loading) {
      if (progress === 0) return;
      setProgress(100);
      setEating(true);
      const t = setTimeout(() => setDone(true), 1000);
      return () => clearTimeout(t);
    }

    setProgress(0);
    setEating(false);
    setDone(false);

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 99) return prev;
        if (prev >= 90) return prev + 0.1;
        if (prev >= 70) return prev + 0.3;
        if (prev >= 50) return prev + 0.8;
        return prev + 1.5;
      });
    }, estimatedSeconds * 10);

    return () => clearInterval(interval);
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!loading && done) return null;
  if (!loading && progress === 0) return null;

  const rabbitX = Math.min(progress, 98);

  return (
    <div style={{ marginTop: "16px", padding: "16px", background: "var(--offwhite)", borderRadius: "12px", border: "1.5px solid var(--border)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px", fontSize: "12px", color: "var(--text-muted)" }}>
        <span>{eating ? "🎉 Done! Rabbit got the carrot!" : "Processing your file…"}</span>
        <span style={{ fontWeight: 600 }}>{Math.round(Math.min(progress, 100))}%</span>
      </div>

      <div style={{ position: "relative", height: "48px", background: "#E8D5A0", borderRadius: "8px", overflow: "hidden", border: "2px solid #C9A84C" }}>

        {/* Dotted path */}
        <div style={{ position: "absolute", top: "50%", left: "40px", right: "40px", height: "4px", transform: "translateY(-50%)", background: "repeating-linear-gradient(90deg, #C9A84C 0px, #C9A84C 6px, transparent 6px, transparent 12px)", borderRadius: "2px" }} />

        {/* Progress fill */}
        <div style={{ position: "absolute", top: "50%", left: "40px", width: `calc(${rabbitX}% - 80px)`, maxWidth: "calc(100% - 80px)", height: "6px", transform: "translateY(-50%)", background: "linear-gradient(90deg, #375623, #52B788)", borderRadius: "3px", transition: "width 0.3s ease" }} />

        {/* Rabbit SVG */}
        <div style={{ position: "absolute", top: "50%", left: `calc(${rabbitX}% - 20px)`, transform: "translateY(-50%)", width: "32px", height: "32px", transition: "left 0.3s ease" }}>
          <svg width="32" height="32" viewBox="0 0 16 16" style={{ imageRendering: "pixelated" }}>
            <rect x="2" y="0" width="2" height="4" fill="#F0E0E0"/>
            <rect x="2" y="1" width="1" height="3" fill="#FF9999"/>
            <rect x="5" y="0" width="2" height="4" fill="#F0E0E0"/>
            <rect x="5" y="1" width="1" height="3" fill="#FF9999"/>
            <rect x="1" y="4" width="7" height="5" fill="white"/>
            <rect x="6" y="5" width="1" height="1" fill="#FF0000"/>
            <rect x="7" y="7" width="1" height="1" fill="#FF9999"/>
            <rect x="0" y="7" width="8" height="5" fill="white"/>
            <rect x="0" y="8" width="2" height="2" fill="white"/>
            <rect x="2" y="11" width="2" height="3" fill="white"/>
            <rect x="5" y="11" width="2" height="3" fill="white"/>
            <rect x="1" y="13" width="3" height="1" fill="#F0E0E0"/>
            <rect x="5" y="13" width="3" height="1" fill="#F0E0E0"/>
          </svg>
        </div>

        {/* Carrot SVG */}
        {!eating && (
          <div style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", width: "24px", height: "32px" }}>
            <svg width="24" height="32" viewBox="0 0 12 16" style={{ imageRendering: "pixelated" }}>
              <rect x="3" y="0" width="2" height="1" fill="#2D6A4F"/>
              <rect x="2" y="1" width="4" height="1" fill="#40916C"/>
              <rect x="1" y="2" width="2" height="1" fill="#2D6A4F"/>
              <rect x="5" y="2" width="2" height="1" fill="#2D6A4F"/>
              <rect x="3" y="2" width="2" height="1" fill="#40916C"/>
              <rect x="2" y="3" width="6" height="2" fill="#F4A261"/>
              <rect x="3" y="5" width="5" height="2" fill="#E76F51"/>
              <rect x="3" y="7" width="4" height="2" fill="#F4A261"/>
              <rect x="4" y="9" width="3" height="2" fill="#E76F51"/>
              <rect x="4" y="11" width="2" height="2" fill="#F4A261"/>
              <rect x="5" y="13" width="1" height="2" fill="#E76F51"/>
            </svg>
          </div>
        )}

        {/* Eating sparkle */}
        {eating && (
          <div style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", fontSize: "20px", animation: "rpop 0.5s ease-out forwards" }}>✨</div>
        )}
      </div>

      <style>{`
        @keyframes rpop {
          0%   { transform: translateY(-50%) scale(1); opacity: 1; }
          100% { transform: translateY(-50%) scale(2); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
